import { useRef, useEffect, useState } from "react";
import "./admin.css";
import { Outlet, useNavigate } from "react-router-dom";
import Logo from "../../../ui/atoms/logo/Logo";
import Notification from "../../../ui/atoms/notification/Notification";
import Profile from "../../../ui/atoms/profile/Profile";
import ASSETS from "../../../app/config/assets";
import NavLinkTab from "../../../ui/molecules/navlink-tab/NavLinkTab";
import { useFullscreen } from "../../../app/providers/useFullscreen";
import { useSelector, useDispatch } from "react-redux";
import { AiOutlineFullscreen, AiOutlineFullscreenExit } from "react-icons/ai";
import { RiDashboardHorizontalLine } from "react-icons/ri";
import { MdOutlineAssessment } from "react-icons/md";
import { SlBookOpen } from "react-icons/sl";
import { GoCommentDiscussion } from "react-icons/go";
import { TbSchool } from "react-icons/tb";
import { FaPeopleLine } from "react-icons/fa6";
//
import { onGetMe } from "../../authenticate/services/authentication";
import { onGetNotifications, onGetDiscussions } from "../services/user";
import useAxiosPrivate from "../../../app/providers/context-provider/useAxiosPrivate";
import { socketIo } from "../../../core/socket/socket-client";
import { configurationSlice } from "../../../app/store/slices/configurationSlice";
import Swal from "sweetalert2";
import moment from "moment";

const Admin = () => {
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const wrapperRef = useRef(null);
  const { isFullscreen, enter, exit } = useFullscreen();

  const [notifcationsAlert, setNotifcationsAlert] = useState(0);
  const [messagesAlert, setMessagesAlert] = useState(0);

  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    if (!auth?.accessToken) return;

    const fetchData = async () => {
      await onGetMe(axiosPrivate)
        .then((response) => {
          const _data = response?.data;
          dispatch(configurationSlice.actions.setUserInformations(_data));
          if (!_data?.is_completed) {
            navigate("/signup/complete", { replace: true });
          }
        })
        .catch((error) => {
          if (!error?.response) {
            Swal.fire({
              icon: "error",
              title: "[MainLayout][onGetMe]:Error",
              text: "No server response",
            });
          } else {
            if (error?.response?.status === 403) {
              Swal.fire({
                icon: "info",
                title: "Session Expired",
                text: "Your session was expired. Refresh to get a new one and continue.",
              });
              return;
            }
            Swal.fire({
              icon: "warning",
              title: "[MainLayout][onGetMe]:Warning",
              text: error?.response?.data?.message,
            });
          }
        });
    };

    fetchData();
  }, [auth?.accessToken]);

  const initConnectedUser = useSelector(
    (state) => state.setInitConf.initConnectedUser,
  );
  const _sys_role = initConnectedUser?.sys_role;
  const _inscription_id = initConnectedUser?.inscription;

  const userInformations = useSelector(
    (state) => state.setInitConf.userInformations,
  );

  const toggleFullscreen = () => {
    if (isFullscreen) exit();
    else enter(wrapperRef.current);
  };

  useEffect(() => {
    onRefreshDiscussions();
    onRefreshNotifications();
  }, []);

  useEffect(() => {
    socketIo.emit("authenticate", { inscription_id: _inscription_id });

    const onHandleNewNotification = (data) => {
      if (data?.status) onRefreshNotifications();
    };

    const onHandleNewMessage = (data) => {
      if (data?.status) onRefreshDiscussions();
    };

    socketIo.on("on_new_notification", onHandleNewNotification);
    socketIo.on("on_new_message", onHandleNewMessage);

    return () => {
      socketIo.off("on_new_notification", onHandleNewNotification);
      socketIo.off("on_new_message", onHandleNewMessage);
    };
  }, []);

  const onRefreshNotifications = async () => {
    let isMounted = true;
    const controller = new AbortController();
    const signal = controller.signal;
    const _data = {
      inscription_id: _inscription_id,
      sys_role: _sys_role,
    };

    await onGetNotifications(axiosPrivate, _data, signal).then((response) => {
      const _data = response?.data;
      setNotifcationsAlert(_data?.total_overall_no_read_notifications);
    });

    return () => {
      isMounted = false;
      isMounted && controller.abort();
    };
  };

  const onRefreshDiscussions = async () => {
    const localData = {
      inscription_id: _inscription_id,
      dates: moment(new Date()).format("YYYY-MM-DD HH:mm:ss"),
    };
    await onGetDiscussions(axiosPrivate, localData).then((response) => {
      const _data = response?.data;
      setMessagesAlert(_data?.total_overall_no_read_messages);
    });
  };

  return (
    <div className="student w-screen h-screen" ref={wrapperRef}>
      <div className="a-header w-full">
        <div className="a-h-container w-90 d-flex justify-space-between align-center">
          <div className="a-h-logo">
            <Logo to={null} width={"85px"} />
          </div>
          <div className="a-h-c-options-wrapper">
            <NavLinkTab
              tab_data={[
                {
                  icon: RiDashboardHorizontalLine,
                  title: "Dashboard",
                  nav_to: "/admin",
                  is_exact: true,
                },
                {
                  icon: MdOutlineAssessment,
                  title: "Challenges",
                  nav_to: "/admin/challenges",
                  is_exact: false,
                },
                {
                  icon: SlBookOpen,
                  title: "Challenge Themes",
                  nav_to: "/admin/challenge/themes",
                  is_exact: false,
                },
                {
                  icon: GoCommentDiscussion,
                  title: "Discussions",
                  nav_to: "/admin/discussions",
                  is_exact: false,
                },
                {
                  icon: TbSchool,
                  title: "Schools",
                  nav_to: "/admin/schools",
                  is_exact: false,
                },
                {
                  icon: FaPeopleLine,
                  title: "Team",
                  nav_to: "/admin/users",
                  is_exact: false,
                },
              ]}
            />
            <div className="a-h-c-options">
              <button onClick={toggleFullscreen}>
                {isFullscreen ? (
                  <AiOutlineFullscreenExit className="icon" />
                ) : (
                  <AiOutlineFullscreen className="icon" />
                )}
              </button>
              <Notification
                icon={ASSETS?.icon_notification_png}
                count={notifcationsAlert}
                to={"/admin/discussions"}
              />
              <Notification
                icon={ASSETS?.icon_mail_png}
                count={messagesAlert}
                to={"/admin/discussions/messages"}
              />
              <Profile
                sys_role={"admin"}
                image={userInformations?.thumbnail}
                username={`${userInformations?.firstname} ${userInformations?.lastname}`}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="a-wrapper">
        <Outlet />
      </div>
    </div>
  );
};

export default Admin;
